import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IServiceOption, IPaymentTypeOption } from "../types/payment.types";
import { paymentsApi } from "../api/payment.api";

interface PaymentSessionState {
  selectedServiceId: number | null;
  sessionPrice: number;
  chargeAmount: number;
  serviceName: string;
  serviceOptions: IServiceOption[];
  paymentTypeOptions: IPaymentTypeOption[];
  questionnaireScoreStatusOptions: IPaymentTypeOption[];
  selectedPaymentTypeId: number | null;
  selectedQuestionnaireScoreStatusId: number | null;
  pendingPayment: {
    CompanyServiceId: number;
    amount: number;
    serviceName: string;
    sessionPrice: number;
  } | null;
}

const initialState: PaymentSessionState = {
  selectedServiceId: null,
  sessionPrice: 0,
  chargeAmount: 0,
  serviceName: "",
  serviceOptions: [],
  paymentTypeOptions: [],
  questionnaireScoreStatusOptions: [],
  selectedPaymentTypeId: null,
  selectedQuestionnaireScoreStatusId: 1,
  pendingPayment: null,
};

const paymentSessionSlice = createSlice({
  name: "paymentSession",
  initialState,
  reducers: {
    setPaymentService: (
      state,
      action: PayloadAction<{
        serviceId: number;
        serviceName: string;
        price: number;
      }>
    ) => {
      state.selectedServiceId = action.payload.serviceId;
      state.serviceName = action.payload.serviceName;
      state.sessionPrice = action.payload.price;
      state.chargeAmount = action.payload.price;
    },
    setSessionPrice: (state, action: PayloadAction<number>) => {
      state.sessionPrice = action.payload;
    },
    setChargeAmount: (state, action: PayloadAction<number>) => {
      state.chargeAmount = action.payload;
    },
    setSelectedPaymentType: (state, action: PayloadAction<number | null>) => {
      state.selectedPaymentTypeId = action.payload;
    },
    setSelectedQuestionnaireScoreStatus: (state, action: PayloadAction<number | null>) => {
      state.selectedQuestionnaireScoreStatusId = action.payload;
    },
    setSelectedServiceId: (state, action: PayloadAction<number | null>) => {
      state.selectedServiceId = action.payload;
    },
    setPendingPayment: (
      state,
      action: PayloadAction<{
        CompanyServiceId: number;
        amount: number;
        serviceName: string;
        sessionPrice: number;
      } | null>
    ) => {
      state.pendingPayment = action.payload;
    },
    clearPaymentSession: (state) => {
      state.selectedServiceId = null;
      state.sessionPrice = 0;
      state.chargeAmount = 0;
      state.serviceName = "";
      state.selectedPaymentTypeId = null;
      state.selectedQuestionnaireScoreStatusId = null;
      state.pendingPayment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        paymentsApi.endpoints.getTherapistServicesWithPrices.matchFulfilled,
        (state, action) => {
          state.serviceOptions = action.payload.map((service) => ({
            value: service.serviceId,
            label: `${service.serviceName} - ${service.currencySymbol}${service.priceAfterTax.toFixed(2)}`,
            service,
          }));
        }
      )
      .addMatcher(
        paymentsApi.endpoints.getPaymentTypes.matchFulfilled,
        (state, action) => {
          const activePaymentTypes = action.payload.filter((paymentType) => paymentType.statusId > 0);
          state.paymentTypeOptions = activePaymentTypes.map((paymentType) => ({
            value: paymentType.id,
            label: paymentType.name,
          }));
          if (activePaymentTypes.length > 0 && !state.selectedPaymentTypeId) {
            state.selectedPaymentTypeId = activePaymentTypes[0].id;
          }
        }
      )
      .addMatcher(
        paymentsApi.endpoints.getActiveQuestionnaireScoreStatuses.matchFulfilled,
        (state, action) => {
          state.questionnaireScoreStatusOptions = action?.payload?.map((status) => ({
            value: status.id,
            label: status.name,
          }));
        }
      );
  },
});

export const {
  setSelectedServiceId,
  setPaymentService,
  setSessionPrice,
  setChargeAmount,
  setSelectedPaymentType,
  setSelectedQuestionnaireScoreStatus,
  setPendingPayment,
  clearPaymentSession,
} = paymentSessionSlice.actions;

export default paymentSessionSlice.reducer;